// src/components/navigation/AdminSidebar.tsx
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  LogOut,
  Mail,
  Bell,
  Users,
  ArrowLeft,
  LayoutDashboard,
  Shield,
  CreditCard,
  Handshake,
  Moon,
  Gauge,
  FileText,
  TrendingUp,
  Timer,
  UserCheck,
  Receipt,
  Package,
  Wallet,
  Megaphone,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLogo } from '@/contexts/LogoContext';
import MemoizedLogo from '@/components/branding/MemoizedLogo';

const adminNavigation = [
  {
    section: 'Business',
    items: [
      { name: 'Overview', href: '/admin/dashboard', icon: LayoutDashboard },
      { name: 'P&L', href: '/admin/pnl', icon: TrendingUp },
      { name: 'Free Trials', href: '/admin/trials', icon: Timer },
      { name: 'Subscribers', href: '/admin/subscribers', icon: UserCheck },
      { name: 'Transactions', href: '/admin/transactions', icon: Receipt },
      { name: 'Add-ons', href: '/admin/addons', icon: Package },
      { name: 'Expenses', href: '/admin/expenses', icon: Wallet },
    ],
  },
  {
    section: 'Audience',
    items: [
      { name: 'Audience & Leads', href: '/admin/audience', icon: Megaphone },
      { name: 'User Management', href: '/admin/users', icon: Users },
      { name: 'Dormant Accounts', href: '/admin/dormant', icon: Moon },
      { name: 'Whop', href: '/admin/whop', icon: ShoppingBag },
    ],
  },
  {
    section: 'Operations',
    items: [
      { name: 'Ops Hub', href: '/admin/ops', icon: Gauge },
      { name: 'A2P Queue', href: '/admin/a2p-queue', icon: Shield },
    ],
  },
  {
    section: 'Configuration',
    items: [
      { name: 'Plan Management', href: '/admin/plans', icon: CreditCard },
      { name: 'Partners', href: '/admin/partners', icon: Handshake },
      { name: 'Prompts', href: '/admin/prompts', icon: FileText },
      { name: 'Tutorials', href: '/admin/tutorials', icon: BookOpen },
    ],
  },
];

export default function AdminSidebar() {
  const navigate = useNavigate();
  const { signOut, user, profile } = useAuth();
  const { logoUrl, companyName, isWhiteLabel } = useLogo();
  
  const userDisplayName = profile?.full_name || user?.email;

  const handleLogoutClick = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
      } else {
        toast.success('Logged out successfully');
        navigate('/login');
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  return (
    <div className="flex h-full w-64 flex-col bg-white border-r border-border">
      {/* Logo/Header */}
      <div className="flex items-center justify-center px-4 py-0 border-b border-border">
        <MemoizedLogo
          logoUrl={logoUrl}
          companyName={companyName}
          isWhiteLabel={isWhiteLabel}
          className="h-40 w-auto max-w-full object-contain"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
        {/* Back to Dashboard */}
        <NavLink
          to="/dashboard/dashboard"
          className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mb-3"
        >
          <ArrowLeft className="mr-3 h-4 w-4 flex-shrink-0" />
          Back to Dashboard
        </NavLink>

        {adminNavigation.map((group) => (
          <div key={group.section} className="pb-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-3">
              {group.section}
            </h3>
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `w-full group flex items-center px-3 py-2 border-r-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary border-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted border-r-white'
                    }`
                  }
                >
                  <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {item.name}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        {/* User Info */}
        <div className="rounded-lg p-3 mb-3 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center text-sm font-medium text-foreground mb-1">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                <span className="truncate">{userDisplayName}</span>
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Mail className="w-3 h-3 mr-1.5 flex-shrink-0" />
                <span className="truncate">Admin</span>
              </div>
            </div>
            <div className="ml-3">
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-background/80 transition-all duration-200"
              >
                <Bell className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent/70 transition-all duration-200"
          onClick={handleLogoutClick}
        >
          <LogOut className="mr-3 h-5 w-5" aria-hidden="true" />
          Logout
        </Button>
      </div>
    </div>
  );
}